import {
  duracaoEmSegundos,
} from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/pcm-em-chunks'

// ★ O AVATAR COM A CARA DE UM `<audio>` — spec-009, a boas-vindas falada pelo
// rosto ao vivo.
//
// ## Por que um adaptador, e não um `if` dentro do player
//
// `boas-vindas-falada.vue` já sabe reger uma fala: ele chama `play()`, lê
// `currentTime`/`duration` para acender o bloco da legenda, ouve `timeupdate`,
// `ended` e `error`, e chama `pause()` no "Continuar". Tudo isso é a
// superfície de um `HTMLAudioElement`.
//
// Então o avatar não precisa de um caminho próprio lá dentro: precisa de um
// objeto que **responda à mesma superfície**. Com isso o player ganha o rosto
// falando sem que `tentarTocar`, `aoProgredir`, `aoTerminar`, `pausarFala` e
// `encerrarAudio` mudem uma linha — e apagar este arquivo devolve o
// comportamento anterior inteiro.
//
// ## O que NÃO dá para imitar, e como isso é resolvido
//
// ⚠️ **Não há `currentTime` do outro lado.** O áudio toca no provedor, não
// aqui; o `agent.speak_started` diz que começou e o `agent.speak_ended` diz
// que acabou, e nada entre os dois. Mas a DURAÇÃO é conhecida com exatidão
// antes de tocar: PCM 16-bit 24 kHz mono tem `24000 * 2` bytes por segundo, e
// o `duracaoEmSegundos` lê isso do próprio base64 sem decodificar nada. O
// relógio local sobre uma duração exata é o que move a legenda.
//
// ⚠️ **O `ended` é o do PROVEDOR, nunca o do relógio.** O relógio é uma
// estimativa de progresso; o fim é um fato, e é ele que dispara o `@fim` da
// US-3. Se a sessão cair no meio (teto do provedor, rede), `falar` resolve e o
// `ended` sai junto — a apresentação termina cedo, visivelmente, em vez de
// deixar o player esperando para sempre por um áudio que ninguém vai tocar.

/** ~20 quadros por segundo: o suficiente para a legenda não pular, e uma
 *  ordem de grandeza abaixo do `timeupdate` real (que dispara a cada ~250 ms
 *  em vários browsers). */
const MS_ENTRE_PROGRESSOS = 50

/**
 * Monta o objeto com a superfície de `<audio>` que fala pelo avatar.
 *
 * @param {Object} opts
 * @param {string} opts.pcmBase64 o áudio da apresentação, em PCM 24 kHz.
 * @param {Function} opts.falar `(pcmBase64) => Promise<void>` — resolve quando
 *   o avatar TERMINA de falar (ou quando a sessão morre).
 * @param {Function} [opts.interromper] corta a fala no provedor.
 * @param {Function} [opts.agora] só para teste: a fonte do relógio.
 * @returns {Object} `{play, pause, currentTime, duration, addEventListener,
 *   removeEventListener}`.
 */
export const criarFalanteComoAudio = ({
  pcmBase64,
  falar,
  interromper,
  agora = () => Date.now(),
}) => {
  const duracao = duracaoEmSegundos(pcmBase64)
  const ouvintes = {}
  let inicio = null
  let decorridoAoPausar = 0
  let relogio = null
  let encerrado = false

  const avisar = (evento) => {
    const cb = ouvintes[evento]
    if (typeof cb !== 'function') return
    try {
      cb()
    } catch (e) {
      // Um ouvinte que estoura não pode derrubar a apresentação.
    }
  }

  const pararRelogio = () => {
    clearInterval(relogio)
    relogio = null
  }

  const falante = {
    // Lidos por `aoProgredir` para calcular o bloco aceso da legenda.
    get currentTime() {
      if (inicio === null) return decorridoAoPausar
      const corrido = decorridoAoPausar + (agora() - inicio) / 1000
      // Nunca passa da duração: `progressoDoAudio` ficaria acima de 1 e a
      // legenda acenderia um bloco que não existe.
      return Math.min(corrido, duracao)
    },
    get duration() {
      return duracao
    },

    play() {
      if (encerrado) return Promise.resolve()
      inicio = agora()
      relogio = setInterval(() => avisar('timeupdate'), MS_ENTRE_PROGRESSOS)

      // ⚠️ O áudio sai AGORA, não num microtask: cada volta do laço de
      // eventos é atraso entre o consultor clicar e o avatar abrir a boca.
      // O `try` cobre um `falar` que estoure de forma síncrona.
      let emVoo
      try {
        emVoo = Promise.resolve(falar(pcmBase64))
      } catch (e) {
        emVoo = Promise.reject(e)
      }

      emVoo
        .catch(() => {
          // Falha do provedor no meio da apresentação: o texto já está na
          // tela, e o player trata `error` mostrando o aviso de "sem áudio".
          if (!encerrado) avisar('error')
        })
        .then(() => {
          if (encerrado) return
          pararRelogio()
          inicio = null
          decorridoAoPausar = duracao
          avisar('ended')
        })

      // ⚠️ O `play()` de um `<audio>` resolve quando a reprodução COMEÇA, não
      // quando termina — e é isso que `tentarTocar` espera para marcar
      // `tocando` e reportar o canal. Devolver a promessa do provedor faria a
      // tela dizer "não está tocando" a apresentação inteira.
      return Promise.resolve()
    },

    pause() {
      if (inicio !== null) {
        decorridoAoPausar += (agora() - inicio) / 1000
        inicio = null
      }
      pararRelogio()
      // O provedor precisa do barge-in DELE: parar o relógio aqui não apaga o
      // áudio que já está no buffer dele, e o avatar seguiria falando.
      if (typeof interromper === 'function') interromper()
    },

    addEventListener(evento, callback) {
      ouvintes[evento] = callback
    },

    removeEventListener(evento) {
      delete ouvintes[evento]
    },

    /** Não existe em `<audio>`; é o que `encerrarAudio` do player alcança
     *  através do `pause()`. Aqui só garante que nada dispare depois. */
    encerrar() {
      encerrado = true
      pararRelogio()
    },
  }

  return falante
}
